import { type AxiosError, isAxiosError } from "axios"
import type { FileUpload, MultipartFileUpload } from "./types"
import type {
  ConstraintViolation,
  ConstraintViolationError,
  HydraCollection,
} from "common/api/types"
import * as R from "remeda"

export function isAPIError(error: unknown): error is AxiosError {
  return isAxiosError(error)
}

export function isConstraintViolationError(
  error: unknown,
): error is ConstraintViolationError {
  if (!isAPIError(error)) {
    return false
  }

  if (!error.response) {
    return false
  }

  if (!error.response.data) {
    return false
  }

  return (
    R.isPlainObject(error.response.data) &&
    (error.response.data["@type"] === "ConstraintViolationList" ||
      error.response.data["@type"] === "ConstraintViolation")
  )
}

export function getViolationsFromError(
  error: unknown,
): Array<ConstraintViolation> {
  if (!isConstraintViolationError(error)) {
    return []
  }

  return error.response?.data.violations ?? []
}

export function isPaginated<EntityType>(
  collection: HydraCollection<EntityType>,
) {
  return hasPreviousPage(collection) || hasNextPage(collection)
}

export function hasPreviousPage<EntityType>(
  collection: HydraCollection<EntityType>,
) {
  return collection["hydra:view"]?.["hydra:previous"] !== undefined
}

export function hasNextPage<EntityType>(
  collection: HydraCollection<EntityType>,
) {
  return collection["hydra:view"]?.["hydra:next"] !== undefined
}

export function getIDFromIRI(iri: `/api/internal/${string}/${number}`) {
  const id = iri.split("/").at(-1)

  if (id === undefined) {
    throw new Error(`Couldn't extract ID from IRI ${iri}`)
  }

  const idAsNumber = Number(id)

  if (isNaN(idAsNumber)) {
    throw new Error(`Extracted ID from IRI ${iri} is not a number`)
  }

  return idAsNumber
}

export function toMultipartFileUpload(
  file: FileUpload | null | undefined,
): MultipartFileUpload | undefined {
  return file === null ? "null" : file
}

export function getDebugTokenLink(error: AxiosError) {
  // @ts-expect-error
  return error.response?.headers.get("X-Debug-Token-Link")
}

export function makeIRI<ID extends number, Endpoint extends string>(
  id: ID,
  endpoint: Endpoint,
): `${Endpoint}/${ID}` {
  return `${endpoint}/${id}`
}
